import ProfileCardClient from '@atlaskit/profilecard/client';
import ProfileCard from '@atlaskit/profilecard/user';
import { ViewComponentProps } from '@databases/fields/view/types';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseFieldType, USER_VALUE_CURRENT } from '@databases/types/database';
import { isNonEmptyUserValue } from '@databases/utils/database/field-types/user';
import { selectCurrentUserId } from '@databases/utils/redux/context/selectors';
import { generateId } from '@databases/utils/utils';
import React, { FunctionComponent, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import UserName, { renderUserMention } from '../../UserName';

const Container = styled.ul`
  list-style-type: none;
  padding: 0;

  > li + li {
    margin-top: 6px;
  }
`;

const UserValueView: FunctionComponent<ViewComponentProps<DatabaseFieldType.USER>> = ({
  value,
}) => {
  const currentUserId = defaultStoreUtils.useSelector(selectCurrentUserId);
  const { formatMessage } = useIntl();
  const resolvedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value.map((user) => {
        if (
          'accountId' in user &&
          user.accountId === USER_VALUE_CURRENT &&
          currentUserId
        ) {
          return { _id: generateId(), accountId: currentUserId };
        } else {
          return user;
        }
      });
    } else if (value === USER_VALUE_CURRENT && currentUserId) {
      return [{ _id: generateId(), accountId: currentUserId }];
    } else {
      return [];
    }
  }, [currentUserId, value]);

  const resourceClient = new ProfileCardClient({
    url: '/gateway/api/directory/graphql',
    productIdentifier: 'confluence',
  });

  return (
    <Container>
      {resolvedValue.filter(isNonEmptyUserValue).map((user, idx) => (
        <li key={user._id}>
          {idx > 0 &&
            // eslint-disable-next-line formatjs/no-literal-string-in-jsx
            ' '}
          <ProfileCard
            resourceClient={resourceClient}
            userId={user.accountId}
            actions={[
              {
                label: formatMessage(
                  messages['user-value-value.profile-card-view-profile-label']
                ),
                callback: () =>
                  window.open(
                    `/wiki/people/${user.accountId}?ref=confluence`,
                    '_blank',
                    'noopener,noreferrer'
                  ),
              },
            ]}
            viewingUserId={currentUserId}
          >
            <UserName
              accountId={user.accountId}
              displayName={user.displayName}
              renderUser={renderUserMention}
            />
          </ProfileCard>
        </li>
      ))}
    </Container>
  );
};

export default UserValueView;
